'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export type CookiePreferences = {
  necessary: boolean
  analytics: boolean
  functionality: boolean
  marketing: boolean
}

type ConsentState = {
  preferences: CookiePreferences
  timestamp: string
  version: string
}

type CookieConsentContextType = {
  consent: ConsentState | null
  showBanner: boolean
  showSettings: boolean
  openSettings: () => void
  closeSettings: () => void
  acceptAll: () => void
  rejectAll: () => void
  savePreferences: (prefs: CookiePreferences) => void
}

const STORAGE_KEY = 'airlovers_cookie_consent'
const CONSENT_VERSION = '1.0.0'

// Default value para SSR: el banner se muestra como no-consentido
const defaultContextValue: CookieConsentContextType = {
  consent: null,
  showBanner: false,
  showSettings: false,
  openSettings: () => {},
  closeSettings: () => {},
  acceptAll: () => {},
  rejectAll: () => {},
  savePreferences: () => {},
}

const CookieConsentContext = createContext<CookieConsentContextType>(defaultContextValue)

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentState | null>(null)
  const [showBanner, setShowBanner] = useState(false)
  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as ConsentState
        if (parsed.version === CONSENT_VERSION) {
          setConsent(parsed)
          applyConsent(parsed.preferences)
        } else {
          setShowBanner(true)
        }
      } else {
        setShowBanner(true)
      }
    } catch {
      setShowBanner(true)
    }
  }, [])

  const persist = (prefs: CookiePreferences) => {
    const newConsent: ConsentState = {
      preferences: prefs,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    }
    setConsent(newConsent)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newConsent))
    } catch {
      // localStorage no disponible, ignorar
    }
    applyConsent(prefs)
  }

  const applyConsent = (prefs: CookiePreferences) => {
    if (typeof window === 'undefined') return
    const w = window as any

    if (prefs.analytics) {
      w.gtag?.('consent', 'update', { analytics_storage: 'granted' })
    } else {
      w.gtag?.('consent', 'update', { analytics_storage: 'denied' })
    }

    if (prefs.marketing) {
      w.gtag?.('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
      })
    } else {
      w.gtag?.('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      })
    }
  }

  const acceptAll = () => {
    persist({ necessary: true, analytics: true, functionality: true, marketing: true })
    setShowBanner(false)
    setShowSettings(false)
  }

  const rejectAll = () => {
    persist({ necessary: true, analytics: false, functionality: false, marketing: false })
    setShowBanner(false)
    setShowSettings(false)
  }

  const savePreferences = (prefs: CookiePreferences) => {
    persist({ ...prefs, necessary: true })
    setShowBanner(false)
    setShowSettings(false)
  }

  const openSettings = () => setShowSettings(true)
  const closeSettings = () => setShowSettings(false)

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        showBanner,
        showSettings,
        openSettings,
        closeSettings,
        acceptAll,
        rejectAll,
        savePreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  )
}

export function useCookieConsent() {
  return useContext(CookieConsentContext)
}
